import { Polygon, Circle, Popup } from 'react-leaflet'

import { useZones, type CustomZone, type ZoneCategory } from '../../hooks/useWorkforce'

/// Wave 1 E1.3 — Live Map 2.0 layers.
///
/// Renders user-defined custom zones (depot / customer / forbidden /
/// low-speed) on top of the geofence layer, with category-specific colours
/// so dispatchers can immediately distinguish "this is a customer drop"
/// from "this is a no-go area" at a glance. Falls back to a neutral
/// shape when categories are missing.
export function MapZonesLayer({ enabled = true }: { enabled?: boolean }) {
  const { data: zones = [] } = useZones()
  if (!enabled) return null

  return (
    <>
      {zones.map((z) => {
        const fill = zoneCategoryColor(z.category) ?? z.color ?? '#7c3aed'
        const geo = z.geometry as { coords?: number[][]; center?: number[]; radius_m?: number }
        if (z.shape === 'polygon' && geo?.coords?.length) {
          return (
            <Polygon
              key={z.id}
              positions={geo.coords as [number, number][]}
              pathOptions={{ color: fill, weight: 2, fillOpacity: 0.18, dashArray: zoneDash(z.category) }}
            >
              <Popup>
                <ZonePopup zone={z} />
              </Popup>
            </Polygon>
          )
        }
        if (z.shape === 'circle' && geo?.center && geo?.radius_m) {
          return (
            <Circle
              key={z.id}
              center={geo.center as [number, number]}
              radius={geo.radius_m}
              pathOptions={{ color: fill, weight: 2, fillOpacity: 0.18, dashArray: zoneDash(z.category) }}
            >
              <Popup>
                <ZonePopup zone={z} />
              </Popup>
            </Circle>
          )
        }
        return null
      })}
    </>
  )
}

function ZonePopup({ zone }: { zone: CustomZone }) {
  return (
    <div>
      <strong>{zone.name}</strong>
      <br />
      <span style={{ textTransform: 'capitalize' }}>{zone.category.replace('_', ' ')}</span>
      {zone.speed_limit_kph != null && zone.category === 'low_speed' && (
        <>
          <br />
          <small>Speed limit {zone.speed_limit_kph} km/h</small>
        </>
      )}
    </div>
  )
}

export function zoneCategoryColor(category: ZoneCategory): string | null {
  switch (category) {
    case 'depot':
      return '#0ea5e9'
    case 'customer':
      return '#22c55e'
    case 'forbidden':
      return '#dc2626'
    case 'low_speed':
      return '#f59e0b'
    default:
      return null
  }
}

function zoneDash(category: ZoneCategory): string | undefined {
  return category === 'forbidden' ? '6 4' : undefined
}
